import { motion } from "framer-motion";
import { Search, TrendingUp, AlertTriangle, ChefHat, MessageCircle, Printer } from "lucide-react";

export const StaffAssistant = () => {
  const chips = [
    { label: "Upsell Tips", icon: TrendingUp },
    { label: "Allergy Check", icon: AlertTriangle },
    { label: "Recipe Steps", icon: ChefHat },
  ];

  return (
    <section data-testid="staff-assistant-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Info */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <span className="font-inter text-xs font-bold tracking-[0.3em] uppercase text-lime">Real-Time Staff Assistant</span>
            <h2 className="font-inter text-4xl md:text-5xl font-black text-white mt-4 mb-6">
              Ask Argus <span className="text-gold">Anything.</span>
            </h2>
            <p className="text-slate-300 leading-relaxed mb-6">
              Every answer comes from your Masterclass database — Argus only speaks the truth as defined by the Chef. No hallucinations. No guesswork.
            </p>

            {/* Ambient POS */}
            <div className="glass-card rounded-xl p-6 border border-gold/10 mb-6" data-testid="ambient-pos-card">
              <div className="flex items-center gap-3 mb-3">
                <MessageCircle className="w-5 h-5 text-blue-400" />
                <span className="font-inter text-sm font-bold text-white">Natural Language Ordering</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-3">
                No more hunting for POS buttons. Argus listens to the server-guest conversation, confirms the order, and fires it to the kitchen automatically.
              </p>
              <div className="flex items-center gap-2">
                <Printer className="w-4 h-4 text-gold" />
                <span className="text-xs text-gold font-mono">Works with Toast, Square, Clover & more</span>
              </div>
            </div>

            <p className="text-sm text-slate-500 italic">
              "Reclaim the art of the table. 100% eye contact. 0% data entry."
            </p>
          </motion.div>

          {/* Right: Phone mockup */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="max-w-xs mx-auto">
              <div className="bg-indigo-700/60 rounded-3xl border-2 border-gold/20 p-4 shadow-2xl">
                {/* Status bar */}
                <div className="flex justify-between items-center mb-4 px-2">
                  <span className="text-xs text-slate-500 font-mono">9:41</span>
                  <div className="w-20 h-5 bg-indigo-900 rounded-full" />
                  <span className="text-xs text-slate-500 font-mono">100%</span>
                </div>

                {/* Header */}
                <div className="text-center mb-6">
                  <p className="text-sm font-inter font-bold text-gold">Argus Xenia</p>
                  <span className="text-xs text-lime font-mono">AI Active</span>
                </div>

                {/* Search bar */}
                <div className="flex items-center gap-2 bg-indigo-900/80 rounded-lg px-4 py-3 mb-4 border border-gold/10">
                  <Search className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-500">Ask Argus anything...</span>
                </div>

                {/* Chips */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {chips.map((chip) => {
                    const ChipIcon = chip.icon;
                    return (
                      <div key={chip.label} data-testid={`chip-${chip.label.toLowerCase().replace(' ', '-')}`}
                        className="flex items-center gap-1.5 bg-gold/10 border border-gold/20 rounded-full px-3 py-1.5">
                        <ChipIcon className="w-3 h-3 text-gold" />
                        <span className="text-xs text-gold font-medium">{chip.label}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Sample response */}
                <div className="bg-lime/5 border border-lime/20 rounded-lg p-3">
                  <p className="text-xs text-slate-300 leading-relaxed">
                    <span className="text-lime font-bold">Argus:</span> The Pan-Seared Salmon pairs best with our Sancerre. Mention the wine flight for a $12 upsell. Chef's tip: present the dish with herb garnish facing the guest.
                  </p>
                </div>

                {/* Home indicator */}
                <div className="mt-6 flex justify-center">
                  <div className="w-24 h-1 bg-slate-600 rounded-full" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
